import type { ColDef, ValueGetterParams, GetQuickFilterTextParams, ValueFormatterParams } from 'ag-grid-enterprise'
import type { IClusterApp } from '~/types/cluster'

import ClusterApplicationCellRenderer from '~/components/CellRenderers/ClusterApplicationCellRenderer'
import ClusterVariationCellRenderer from '~/components/CellRenderers/ClusterVariationCellRenderer'
import ScreenshotsCellRenderer from '~/components/CellRenderers/ScreenshotsCellRenderer'
import sharedHeaderTemplate from '~/components/HeaderTemplate'
import { formatDecimalNumber, formatValue } from '~/utils/number'
import { detailedScoreColDef } from '~/utils/table'

export const COLUMNS = {
  APPLICATION: 'name',
  ARPDL_D30: 'arpdl_d30',
  ARPDL_D30_US: 'arpdl_d30_us',
  COMPANY_DOWNLOADS_D30: 'company_downloads_d30',
  COMPANY_REVENUE_D30: 'company_revenue_d30',
  DOWNLOADS_D180: 'downloads_d180',
  DOWNLOADS_D180_US: 'downloads_d180_us',
  DOWNLOADS_D30: 'downloads_d30',
  DOWNLOADS_D30_US: 'downloads_d30_us',
  DOWNLOADS_D360: 'downloads_d360',
  DOWNLOADS_D360_US: 'downloads_d360_us',
  DOWNLOADS_D90: 'downloads_d90',
  DOWNLOADS_D90_US: 'downloads_d90_us',
  DOWNLOADS_TRENDS_D7_D28: 'downloads_trends_d7_d28',
  DOWNLOADS_TRENDS_D7_D28_US: 'downloads_trends_d7_d28_us',
  GROWTH_DOWNLOADS: 'growth_downloads',
  GROWTH_DOWNLOADS_US: 'growth_downloads_us',
  GROWTH_REVENUE: 'growth_revenue',
  GROWTH_REVENUE_US: 'growth_revenue_us',
  REVENUE_D180: 'revenue_d180',
  REVENUE_D180_US: 'revenue_d180_us',
  REVENUE_D30: 'revenue_d30',
  REVENUE_D30_US: 'revenue_d30_us',
  REVENUE_D360: 'revenue_d360',
  REVENUE_D360_US: 'revenue_d360_us',
  REVENUE_D90: 'revenue_d90',
  REVENUE_D90_US: 'revenue_d90_us',
  REVENUE_TRENDS_D7_D28: 'revenue_trends_d7_d28',
  REVENUE_TRENDS_D7_D28_US: 'revenue_trends_d7_d28_us',
  RPD: 'rpd',
  RPD_US: 'rpd_us',
  SCORE: 'score_app_total',
  SCORE_APP_INFO: 'score_app_info',
  SCORE_COMPANY_INFO: 'score_company_info',
  SCORE_DATA: 'score_data',
  SCORE_MONET: 'score_monetization',
  SCORE_PRODUCT: 'score_product',
  SCREENSHOTS: 'screenshot_urls',
}

const COLUMN_WIDTH = {
  EXTRA_LARGE: 700,
  LARGE: 300,
  SMALL: 130,
}

const numericColumnDefinition: ColDef = {
  cellClass: 'numeric-value',
  filter: 'agNumberColumnFilter',
  filterParams: {
    filterOptions: ['lessThan', 'greaterThan', 'inRange'],
  },
  suppressSizeToFit: true,
  width: COLUMN_WIDTH.SMALL,
}

const columns: ColDef[] = [
  {
    cellRenderer: ClusterApplicationCellRenderer,
    comparator: (a: IClusterApp, b: IClusterApp): number => {
      if (!a.name?.length) return 1
      if (!b.name?.length) return -1

      return a.name.toLowerCase().localeCompare(b.name.toLowerCase())
    },
    field: COLUMNS.APPLICATION,
    filter: 'agTextColumnFilter',
    filterValueGetter: ({ data }: ValueGetterParams<IClusterApp>): string => {
      const { name, product_id_data_ai, publisher_name, store_id } = data || {}
      return `${name || ''} ${product_id_data_ai || ''} ${publisher_name || ''} ${store_id || ''}`
    },
    getQuickFilterText: ({ value }: GetQuickFilterTextParams) => value,
    headerName: 'Application',
    minWidth: COLUMN_WIDTH.SMALL,
    pinned: 'left',
    suppressSizeToFit: true,
    valueGetter: ({ data }: ValueGetterParams<IClusterApp>) => data,
    width: COLUMN_WIDTH.LARGE,
  },
  detailedScoreColDef({
    ...numericColumnDefinition,
    field: COLUMNS.SCORE,
    filterValueGetter: ({ data }: ValueGetterParams<IClusterApp>) =>
      data?.score_app_total ? `${data.score_app_total}` : undefined,
    headerName: 'Score',
    valueGetter: ({ data }: ValueGetterParams<IClusterApp>) => {
      if (!data) return undefined

      return {
        cellScore: { label: 'Total', value: data.score_app_total || 0 },
        scale: 100,
        tooltipScores: [
          { label: 'Product', value: data.score_product || 0 },
          { label: 'App Info', value: data.score_app_info || 0 },
          { label: 'Company Info', value: data.score_company_info || 0 },
          { label: 'Data', value: data.score_data || 0 },
          { label: 'Monet', value: data.score_monetization || 0 },
        ],
      }
    },
    width: COLUMN_WIDTH.SMALL,
  }),
  {
    cellRenderer: ScreenshotsCellRenderer,
    field: COLUMNS.SCREENSHOTS,
    headerName: 'Screenshots',
    menuTabs: [],
    sortable: false,
    suppressSizeToFit: true,
    width: COLUMN_WIDTH.EXTRA_LARGE,
  },
  detailedScoreColDef({
    ...numericColumnDefinition,
    field: COLUMNS.SCORE_PRODUCT,
    filterValueGetter: ({ data }: ValueGetterParams<IClusterApp>) =>
      data?.score_product ? `${data.score_product}` : undefined,
    headerName: 'Score Product',
    suppressSizeToFit: true,
    valueGetter: ({ data }: ValueGetterParams<IClusterApp>) => {
      if (!data) return undefined

      return {
        cellScore: { label: 'Product', value: data.score_product || 0 },
        scale: 100,
      }
    },
  }),
  detailedScoreColDef({
    ...numericColumnDefinition,
    field: COLUMNS.SCORE_APP_INFO,
    filterValueGetter: ({ data }: ValueGetterParams<IClusterApp>) =>
      data?.score_app_info ? `${data.score_app_info}` : undefined,
    headerName: 'Score App Info',
    suppressSizeToFit: true,
    valueGetter: ({ data }: ValueGetterParams<IClusterApp>) => {
      if (!data) return undefined

      return {
        cellScore: { label: 'App Info', value: data.score_app_info || 0 },
        scale: 100,
      }
    },
  }),
  detailedScoreColDef({
    ...numericColumnDefinition,
    field: COLUMNS.SCORE_COMPANY_INFO,
    filterValueGetter: ({ data }: ValueGetterParams<IClusterApp>) =>
      data?.score_company_info ? `${data.score_company_info}` : undefined,
    headerName: 'Score Company Info',
    suppressSizeToFit: true,
    valueGetter: ({ data }: ValueGetterParams<IClusterApp>) => {
      if (!data) return undefined

      return {
        cellScore: { label: 'Company Info', value: data.score_company_info || 0 },
        scale: 100,
      }
    },
  }),
  detailedScoreColDef({
    ...numericColumnDefinition,
    field: COLUMNS.SCORE_DATA,
    filterValueGetter: ({ data }: ValueGetterParams<IClusterApp>) =>
      data?.score_data ? `${data.score_data}` : undefined,
    headerName: 'Score Data',
    suppressSizeToFit: true,
    valueGetter: ({ data }: ValueGetterParams<IClusterApp>) => {
      if (!data) return undefined

      return {
        cellScore: { label: 'Data', value: data.score_data || 0 },
        scale: 100,
      }
    },
  }),
  detailedScoreColDef({
    ...numericColumnDefinition,
    field: COLUMNS.SCORE_MONET,
    filterValueGetter: ({ data }: ValueGetterParams<IClusterApp>) =>
      data?.score_monetization ? `${data.score_monetization}` : undefined,
    headerName: 'Score Monet',
    suppressSizeToFit: true,
    valueGetter: ({ data }: ValueGetterParams<IClusterApp>) => {
      if (!data) return undefined

      return {
        cellScore: { label: 'Monet', value: data.score_monetization || 0 },
        scale: 100,
      }
    },
  }),
  {
    ...numericColumnDefinition,
    field: COLUMNS.RPD,
    headerName: 'RPD',
    valueFormatter: ({ value }: ValueFormatterParams<Pick<IClusterApp, 'rpd'>>) =>
      value ? `${formatDecimalNumber(value, 2)}$` : '',
  },
  {
    ...numericColumnDefinition,
    field: COLUMNS.RPD_US,
    headerName: 'RPD 🇺🇸',
    valueFormatter: ({ value }: ValueFormatterParams<Pick<IClusterApp, 'rpd_us'>>) =>
      value ? `${formatDecimalNumber(value, 2)}$` : '',
  },
  {
    ...numericColumnDefinition,
    field: COLUMNS.ARPDL_D30,
    headerName: 'ARPDL D30',
    valueFormatter: ({ value }: ValueFormatterParams<Pick<IClusterApp, 'arpdl_d30'>>) =>
      value ? `${formatDecimalNumber(value, 2)}$` : '',
  },
  {
    ...numericColumnDefinition,
    field: COLUMNS.ARPDL_D30_US,
    headerName: 'ARPDL D30 🇺🇸',
    valueFormatter: ({ value }: ValueFormatterParams<Pick<IClusterApp, 'arpdl_d30_us'>>) =>
      value ? `${formatDecimalNumber(value, 2)}$` : '',
  },
  {
    ...numericColumnDefinition,
    cellRenderer: ClusterVariationCellRenderer,
    field: COLUMNS.DOWNLOADS_TRENDS_D7_D28,
    headerComponentParams: {
      template: sharedHeaderTemplate({ headerSubtitle: 'D7 / D28', headerTitle: 'DL Trends' }),
    },
    headerName: '',
    valueGetter: ({ data }: ValueGetterParams<IClusterApp>) =>
      data && data.downloads_trends_d7_d28 ? data.downloads_trends_d7_d28 * 100 : undefined,
  },
  {
    ...numericColumnDefinition,
    cellRenderer: ClusterVariationCellRenderer,
    field: COLUMNS.REVENUE_TRENDS_D7_D28,
    headerComponentParams: {
      template: sharedHeaderTemplate({ headerSubtitle: 'D7 / D28', headerTitle: 'REV Trends' }),
    },
    headerName: '',
    valueGetter: ({ data }: ValueGetterParams<IClusterApp>) =>
      data && data.revenue_trends_d7_d28 ? data.revenue_trends_d7_d28 * 100 : undefined,
  },
  {
    ...numericColumnDefinition,
    cellRenderer: ClusterVariationCellRenderer,
    field: COLUMNS.DOWNLOADS_TRENDS_D7_D28_US,
    headerComponentParams: {
      template: sharedHeaderTemplate({ headerSubtitle: 'D7 / D28 🇺🇸', headerTitle: 'DL Trends' }),
    },
    headerName: '',
    valueGetter: ({ data }: ValueGetterParams<IClusterApp>) =>
      data && data.downloads_trends_d7_d28_us ? data.downloads_trends_d7_d28_us * 100 : undefined,
  },
  {
    ...numericColumnDefinition,
    cellRenderer: ClusterVariationCellRenderer,
    field: COLUMNS.REVENUE_TRENDS_D7_D28_US,
    headerComponentParams: {
      template: sharedHeaderTemplate({ headerSubtitle: 'D7 / D28 🇺🇸', headerTitle: 'REV Trends' }),
    },
    headerName: '',
    valueGetter: ({ data }: ValueGetterParams<IClusterApp>) =>
      data && data.revenue_trends_d7_d28_us ? data.revenue_trends_d7_d28_us * 100 : undefined,
  },
  {
    ...numericColumnDefinition,
    field: COLUMNS.DOWNLOADS_D30,
    headerComponentParams: {
      template: sharedHeaderTemplate({ headerSubtitle: 'D30', headerTitle: 'Downloads' }),
    },
    headerName: '',
    valueFormatter: ({ value }: ValueFormatterParams<Pick<IClusterApp, 'downloads_d30'>>) =>
      value ? formatValue(value) : '',
  },
  {
    ...numericColumnDefinition,
    field: COLUMNS.REVENUE_D30,
    headerComponentParams: {
      template: sharedHeaderTemplate({ headerSubtitle: 'D30', headerTitle: 'Revenue' }),
    },
    headerName: '',
    valueFormatter: ({ value }: ValueFormatterParams<Pick<IClusterApp, 'revenue_d30'>>) =>
      value ? `${formatValue(value)}$` : '',
  },
  {
    ...numericColumnDefinition,
    field: COLUMNS.DOWNLOADS_D360,
    headerComponentParams: {
      template: sharedHeaderTemplate({ headerSubtitle: 'D360', headerTitle: 'Downloads' }),
    },
    headerName: '',
    valueFormatter: ({ value }: ValueFormatterParams<Pick<IClusterApp, 'downloads_d360'>>) =>
      value ? formatValue(value) : '',
  },
  {
    ...numericColumnDefinition,
    field: COLUMNS.REVENUE_D360,
    headerComponentParams: {
      template: sharedHeaderTemplate({ headerSubtitle: 'D360', headerTitle: 'Revenue' }),
    },
    headerName: '',
    valueFormatter: ({ value }: ValueFormatterParams<Pick<IClusterApp, 'revenue_d360'>>) =>
      value ? `${formatValue(value)}$` : '',
  },
  {
    ...numericColumnDefinition,
    field: COLUMNS.DOWNLOADS_D30_US,
    headerComponentParams: {
      template: sharedHeaderTemplate({ headerSubtitle: 'D30 🇺🇸', headerTitle: 'Downloads' }),
    },
    headerName: '',
    valueFormatter: ({ value }: ValueFormatterParams<Pick<IClusterApp, 'downloads_d30_us'>>) =>
      value ? formatValue(value) : '',
  },
  {
    ...numericColumnDefinition,
    field: COLUMNS.REVENUE_D30_US,
    headerComponentParams: {
      template: sharedHeaderTemplate({ headerSubtitle: 'D30 🇺🇸', headerTitle: 'Revenue' }),
    },
    headerName: '',
    valueFormatter: ({ value }: ValueFormatterParams<Pick<IClusterApp, 'revenue_d30_us'>>) =>
      value ? `${formatValue(value)}$` : '',
  },
  {
    ...numericColumnDefinition,
    field: COLUMNS.DOWNLOADS_D360_US,
    headerComponentParams: {
      template: sharedHeaderTemplate({ headerSubtitle: 'D360 🇺🇸', headerTitle: 'Downloads' }),
    },
    headerName: '',
    valueFormatter: ({ value }: ValueFormatterParams<Pick<IClusterApp, 'downloads_d360_us'>>) =>
      value ? formatValue(value) : '',
  },
  {
    ...numericColumnDefinition,
    field: COLUMNS.REVENUE_D360_US,
    headerComponentParams: {
      template: sharedHeaderTemplate({ headerSubtitle: 'D360 🇺🇸', headerTitle: 'Revenue' }),
    },
    headerName: '',
    valueFormatter: ({ value }: ValueFormatterParams<Pick<IClusterApp, 'revenue_d360_us'>>) =>
      value ? `${formatValue(value)}$` : '',
  },
  {
    ...numericColumnDefinition,
    field: COLUMNS.DOWNLOADS_D180,
    headerComponentParams: {
      template: sharedHeaderTemplate({ headerSubtitle: 'D180', headerTitle: 'Downloads' }),
    },
    headerName: '',
    valueFormatter: ({ value }: ValueFormatterParams<Pick<IClusterApp, 'downloads_d180'>>) =>
      value ? formatValue(value) : '',
  },
  {
    ...numericColumnDefinition,
    field: COLUMNS.REVENUE_D180,
    headerComponentParams: {
      template: sharedHeaderTemplate({ headerSubtitle: 'D180', headerTitle: 'Revenue' }),
    },
    headerName: '',
    valueFormatter: ({ value }: ValueFormatterParams<Pick<IClusterApp, 'revenue_d180'>>) =>
      value ? `${formatValue(value)}$` : '',
  },
  {
    ...numericColumnDefinition,
    field: COLUMNS.DOWNLOADS_D90,
    headerComponentParams: {
      template: sharedHeaderTemplate({ headerSubtitle: 'D90', headerTitle: 'Downloads' }),
    },
    headerName: '',
    valueFormatter: ({ value }: ValueFormatterParams<Pick<IClusterApp, 'downloads_d90'>>) =>
      value ? formatValue(value) : '',
  },
  {
    ...numericColumnDefinition,
    field: COLUMNS.REVENUE_D90,
    headerComponentParams: {
      template: sharedHeaderTemplate({ headerSubtitle: 'D90', headerTitle: 'Revenue' }),
    },
    headerName: '',
    valueFormatter: ({ value }: ValueFormatterParams<Pick<IClusterApp, 'revenue_d90'>>) =>
      value ? `${formatValue(value)}$` : '',
  },
  {
    ...numericColumnDefinition,
    field: COLUMNS.DOWNLOADS_D180_US,
    headerComponentParams: {
      template: sharedHeaderTemplate({ headerSubtitle: 'D180 🇺🇸', headerTitle: 'Downloads' }),
    },
    headerName: '',
    valueFormatter: ({ value }: ValueFormatterParams<Pick<IClusterApp, 'downloads_d180_us'>>) =>
      value ? formatValue(value) : '',
  },
  {
    ...numericColumnDefinition,
    field: COLUMNS.REVENUE_D180_US,
    headerComponentParams: {
      template: sharedHeaderTemplate({ headerSubtitle: 'D180 🇺🇸', headerTitle: 'Revenue' }),
    },
    headerName: '',
    valueFormatter: ({ value }: ValueFormatterParams<Pick<IClusterApp, 'revenue_d180_us'>>) =>
      value ? `${formatValue(value)}$` : '',
  },
  {
    ...numericColumnDefinition,
    field: COLUMNS.DOWNLOADS_D90_US,
    headerComponentParams: {
      template: sharedHeaderTemplate({ headerSubtitle: 'D90 🇺🇸', headerTitle: 'Downloads' }),
    },
    headerName: '',
    valueFormatter: ({ value }: ValueFormatterParams<Pick<IClusterApp, 'downloads_d90_us'>>) =>
      value ? formatValue(value) : '',
  },
  {
    ...numericColumnDefinition,
    field: COLUMNS.REVENUE_D90_US,
    headerComponentParams: {
      template: sharedHeaderTemplate({ headerSubtitle: 'D90 🇺🇸', headerTitle: 'Revenue' }),
    },
    headerName: '',
    valueFormatter: ({ value }: ValueFormatterParams<Pick<IClusterApp, 'revenue_d90_us'>>) =>
      value ? `${formatValue(value)}$` : '',
  },
  {
    ...numericColumnDefinition,
    field: COLUMNS.COMPANY_DOWNLOADS_D30,
    headerComponentParams: {
      template: sharedHeaderTemplate({ headerSubtitle: 'D30', headerTitle: 'Company DL' }),
    },
    headerName: '',
    valueFormatter: ({ value }: ValueFormatterParams<Pick<IClusterApp, 'company_downloads_d30'>>) =>
      value ? formatValue(value) : '',
  },
  {
    ...numericColumnDefinition,
    field: COLUMNS.COMPANY_REVENUE_D30,
    headerComponentParams: {
      template: sharedHeaderTemplate({ headerSubtitle: 'D30', headerTitle: 'Company REV' }),
    },
    headerName: '',
    valueFormatter: ({ value }: ValueFormatterParams<Pick<IClusterApp, 'company_revenue_d30'>>) =>
      value ? `${formatValue(value)}$` : '',
  },
]

export default columns
